import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import YouTube from '@/components/YouTube'
import { findLesson, findCourse, findNextLesson, findPreviousLesson } from '@/models/course.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'
import { formatDuration, formatDurationISO8601 } from '@/utils/number'
import { isGooglebot } from '@/utils/user-agent'

type LoaderData = typeof loader

export async function loader({ params, request, url }: LoaderFunctionArgs) {
  const course = findCourse('vitess')

  if (!course) {
    throw new Response('Not Found', { status: 404 })
  }

  const lesson = findLesson(course.slug as string, params.slug as string)

  if (!lesson || (course.release && course.release < lesson.sequence)) {
    throw new Response('Not Found', { status: 404 })
  }

  const { root } = pageHelper(url)
  const userAgent = request.headers.get('user-agent')
  const googlebot = isGooglebot(userAgent)

  return {
    lesson: lesson,
    course: course,
    nextLesson: findNextLesson(course.slug as string, null, lesson.sequence),
    previousLesson: findPreviousLesson(course.slug as string, null, lesson.sequence),
    root: root,
    googlebot: googlebot
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData, params }) => {
  if (!loaderData) return []

  const {
    course,
    lesson,
    lesson: { meta },
    root
  } = loaderData

  const title = `${meta.title || lesson.title} — ${course.name} — PlanetScale`
  const description = meta.description || lesson.description
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: `https://planetscale.com${lesson.path}` },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@planetscale' },
    { property: 'twitter:creator', content: '@planetscale' },
    { property: 'twitter:url', content: `https://planetscale.com${lesson.path}` },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: `https://planetscale.com${lesson.path}` },
    { tagName: 'link', rel: 'preload', href: lesson.video.url, as: 'document' },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: course.name,
            item: `https://planetscale.com/learn/courses/${params.course}`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: lesson.title
          }
        ]
      }
    },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: lesson.title,
        articleBody: lesson.body_text,
        image: `https://planetscale.com${lesson.meta.image}`,
        dateCreated: lesson.createdAt,
        datePublished: lesson.createdAt,
        dateModified: lesson.updatedAt,
        author: {
          '@type': 'Organization',
          name: 'PlanetScale',
          url: 'planetscale.com'
        },
        video: {
          '@context': 'https://schema.org',
          '@type': 'VideoObject',
          name: lesson.title,
          description: lesson.description,
          duration: formatDurationISO8601(lesson.video.duration),
          embedUrl: lesson.video.url,
          image: `https://planetscale.com${lesson.meta.image}`,
          thumbnailUrl: `https://planetscale.com${lesson.meta.image}`,
          dateCreated: lesson.createdAt,
          uploadDate: lesson.createdAt,
          datePublished: lesson.createdAt,
          dateModified: lesson.updatedAt,
          author: {
            '@type': 'Organization',
            name: 'PlanetScale',
            url: 'planetscale.com'
          }
        }
      }
    },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.name,
        description: course.description,
        image: `https://planetscale.com${lesson.meta.image}`,
        datePublished: course.createdAt,
        inLanguage: 'en',
        provider: {
          '@type': 'Organization',
          name: 'PlanetScale',
          url: 'planetscale.com'
        },
        offers: {
          '@type': 'Offer',
          category: 'Free',
          price: '0',
          priceCurrency: 'USD'
        },
        hasCourseInstance: [
          {
            '@type': 'CourseInstance',
            courseMode: ['Online'],
            courseWorkload: formatDurationISO8601(course.duration),
            instructor: {
              '@type': 'Person',
              jobTitle: course.instructor.title,
              name: course.instructor.name
            }
          }
        ]
      }
    }
  ]
}

export default function CourseLesson() {
  const { course, lesson, nextLesson, previousLesson, googlebot } = useLoaderData<LoaderData>()

  return (
    <div className='flex min-h-screen flex-col justify-between leading-normal'>
      <SiteHeader isFullWidth />

      <header className='flex flex-col items-center justify-center space-y-3 px-1 pb-4 md:px-5 lg:flex-row lg:justify-between lg:gap-3 lg:space-y-0'>
        <Link className='flex-auto shrink-0 leading-none font-semibold text-primary' to={course.path}>
          Learn Vitess
        </Link>
      </header>

      <div className='p-4 pt-0'>
        <div className='flex items-center justify-center'>
          <div className='max-w-(--breakpoint-lg) min-w-0'>
            <div className='flex flex-col gap-5'>
              <main>
                <div className='relative mb-3 flex items-start p-2 text-center leading-none tracking-tight'>
                  {previousLesson && (
                    <Link
                      className='absolute flex items-center gap-[4px] rounded-sm border p-1 text-primary shadow-lg md:items-start'
                      prefetch='intent'
                      rel='prev'
                      to={previousLesson.path}
                    >
                      &larr;
                      <span className='leading-tight text-secondary'>
                        <span aria-hidden='true'>{previousLesson.sequence} </span>
                        <span className='text-primary'>{previousLesson.title}</span>
                      </span>
                    </Link>
                  )}

                  <div className='flex hidden w-full flex-auto flex-col items-center justify-center gap-[4px] px-9 md:block'>
                    <h1 className='inline leading-none font-semibold text-primary'>
                      <span aria-hidden='true' className='font-light leading-none tracking-tight text-secondary'>
                        {lesson.sequence}{' '}
                      </span>
                      {lesson.title}
                    </h1>
                  </div>
                  {nextLesson && (
                    <Link
                      className='absolute right-0 flex items-center gap-[4px] rounded-sm border p-1 text-primary shadow-lg md:items-start'
                      prefetch='intent'
                      rel='next'
                      to={nextLesson.path}
                    >
                      <span className='leading-tight text-secondary'>
                        <span aria-hidden='true'>{nextLesson.sequence} </span>
                        <span className='text-primary'>{nextLesson.title}</span>
                      </span>
                      &rarr;
                    </Link>
                  )}
                </div>

                <div className='relative flex aspect-video w-full items-center justify-center rounded-sm bg-secondary p-[3px] lg:p-2'>
                  <YouTube
                    className='aspect-video h-full w-full rounded-xs bg-primary'
                    src={lesson.video.url}
                    title={lesson.title}
                  />
                </div>

                {!googlebot && (
                  <div className='flex w-full justify-center lg:justify-end'>
                    <Link
                      className='group mx-4 rounded-b border-r border-b border-l px-2 pt-1 pb-1 text-left leading-tight shadow-xs shadow-black/20 transition hover:bg-black/15 sm:text-center md:mx-6 lg:mr-2 lg:pb-1.5'
                      prefetch='intent'
                      to='/contact'
                    >
                      <span className='hidden text-secondary lg:inline'>Want a fully-managed Vitess database? </span>
                      <span className='inline-flex items-center justify-center font-medium text-primary group-hover:text-orange'>
                        Get in touch
                      </span>
                    </Link>
                  </div>
                )}
              </main>

              <div className='container max-w-(--breakpoint-lg) min-w-0 gap-2 px-0'>
                <div className='flex gap-3'>
                  <div aria-hidden='true' className='font-light hidden leading-none tracking-tight text-secondary'>
                    {lesson.sequence}
                  </div>

                  <div className='flex flex-col gap-5 overflow-hidden'>
                    <header className='space-x-2 leading-none tracking-tight md:block lg:space-x-0 lg:tracking-tighter'>
                      <span
                        aria-hidden='true'
                        className='font-light leading-none tracking-tight text-secondary lg:hidden'
                      >
                        {lesson.sequence}
                      </span>
                      <span className='inline font-semibold text-primary'>{lesson.title}</span>
                    </header>

                    <aside className='flex flex-col-reverse gap-0'>
                      <div className='prose prose-invert prose-pre:bg-secondary block max-w-none'>
                        {renderers.react(lesson.body, React, {
                          components: {
                            ContentImage
                          }
                        })}
                      </div>

                      <div className='flex flex-col gap-3 lg:flex-row'>
                        <div className='flex w-full flex-col flex-wrap justify-between gap-2 rounded-sm border p-3 shadow-lg shadow-black/35'>
                          <div className='space-y-1'>
                            <h2 className='leading-none font-semibold text-primary'>About this lesson</h2>
                            <p className='leading-normal text-secondary'>{lesson.description}</p>
                          </div>

                          <div className='flex w-full flex-wrap gap-2 text-secondary'>
                            <div className='flex items-center gap-1'>
                              <svg
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M8 4V7.5L10.5 9'
                                  stroke='#414141'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                                <circle cx='8' cy='8' r='7' stroke='#818181' />
                              </svg>

                              <span>{formatDuration(lesson.video.duration)}</span>
                            </div>

                            <div className='flex items-center gap-1 leading-none'>
                              <svg
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path d='M14.5 6L1.5 6' stroke='#818181' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M6 9.5H4' stroke='#414141' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M8 11.5H4' stroke='#414141' strokeLinecap='round' strokeLinejoin='round' />
                                <path
                                  d='M13.5 2.5C14.0523 2.5 14.5 2.94772 14.5 3.5L14.5 13.5C14.5 14.0523 14.0523 14.5 13.5 14.5L2.5 14.5C1.94771 14.5 1.5 14.0523 1.5 13.5L1.5 3.5C1.5 2.94771 1.94772 2.5 2.5 2.5L13.5 2.5Z'
                                  stroke='#818181'
                                />
                                <path
                                  d='M11.5 1.5L11.5 4'
                                  stroke='#818181'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                                <path d='M8 1.5L8 4' stroke='#818181' strokeLinecap='round' strokeLinejoin='round' />
                                <path
                                  d='M4.5 1.5L4.5 4'
                                  stroke='#818181'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>

                              <time dateTime={lesson.createdAt}>{formatLong(lesson.createdAt)}</time>
                            </div>

                            <div className='flex flex-wrap items-center gap-1'>
                              <div className='rounded-[4px] border border-contrast px-sm py-px text-center font-mono font-medium tracking-widest text-secondary'>
                                <span className='sr-only'>Closed captioned</span>
                                <span aria-hidden='true' className='select-none'>
                                  CC
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </aside>

                    <div className='mb-2'>
                      <p className='text-primary'>
                        Feedback or questions? Reach out to our team at{' '}
                        <a href='mailto:education@planetscale.com'>education@planetscale.com</a>.
                      </p>
                    </div>

                    {(nextLesson || previousLesson) && (
                      <nav className='flex flex-col items-center gap-2 overflow-hidden md:flex-row xl:gap-4'>
                        <div className='flex h-full w-full'>
                          {previousLesson && (
                            <Link
                              className='group flex h-full w-full flex-col flex-wrap items-center rounded-sm border p-3 text-primary shadow-lg hover:bg-secondary md:items-start'
                              prefetch='intent'
                              rel='prev'
                              to={previousLesson.path}
                            >
                              <h3 className='mb-1 text-secondary transition group-hover:text-primary'>
                                <span aria-hidden='true'>&larr;</span>Previous lesson
                              </h3>
                              <span className='text-secondary'>
                                {previousLesson.sequence}{' '}
                                <span className='font-semibold text-primary'>{previousLesson.title}</span>
                              </span>
                            </Link>
                          )}
                        </div>

                        <div className='flex h-full w-full'>
                          {nextLesson && (
                            <Link
                              className='group flex h-full w-full flex-col flex-wrap items-center rounded-sm border p-3 text-primary transition hover:bg-secondary md:items-end md:text-right'
                              prefetch='intent'
                              rel='next'
                              to={nextLesson.path}
                            >
                              <h3 className='mb-1 text-secondary group-hover:text-primary'>
                                Next lesson<span aria-hidden='true'> &rarr;</span>
                              </h3>
                              <span className='text-secondary'>
                                {nextLesson.sequence}{' '}
                                <span className='font-semibold text-primary'>{nextLesson.title}</span>
                              </span>
                            </Link>
                          )}
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  )
}
